import { Injectable, OnDestroy, inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AuthenticationResult,
  EventType,
  InteractionStatus,
} from '@azure/msal-browser';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/internal/operators/filter';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { RoughagePlusEnvironment } from 'src/environments/environment';

import { ToastService } from './toast.service';
import {
  AccessRoleTypes,
  AccessRule,
  AgrUserClaims,
  AgrUserClaimsProperty,
} from '../models/user.model';
import { decompress } from '../shared/utils/decompress';
import UserActions from '../store/user/actions';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService implements OnDestroy {
  private readonly msalService = inject(MsalService);
  private readonly broadcastService = inject(MsalBroadcastService);
  private readonly toastService = inject(ToastService);
  private readonly environment =
    inject<RoughagePlusEnvironment>(EnvironmentInjector);
  private readonly store = inject(Store);

  public readonly destroy$ = new Subject<void>();

  public readonly msalInteractionNone$ = this.broadcastService.inProgress$.pipe(
    takeUntil(this.destroy$),
    filter((status) => status === InteractionStatus.None),
  );

  private readonly b2cAuthorities = {
    customerLeadSignin: `${this.environment.AUTHORITY}/${this.environment.SIGNUP_SIGNIN_POLICY}`,
    leadSignup: `${this.environment.AUTHORITY}/${this.environment.LEAD_SIGNUP_POLICY}`,
    employeeSignin: `${this.environment.AUTHORITY}/${this.environment.EMPLOYEE_SIGNIN_POLICY}`,
  };

  constructor() {
    this.broadcastService.msalSubject$
      .pipe(
        takeUntil(this.destroy$),
        filter((msg) => msg.eventType === EventType.LOGIN_FAILURE),
      )
      .subscribe(() => {
        this.toastService.showWarning(
          'Er is een fout opgetreden bij het inloggen. Probeer het opnieuw.',
        );
      });

    this.broadcastService.msalSubject$
      .pipe(
        takeUntil(this.destroy$),
        filter((msg) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
      )
      .subscribe(() => this.login());

    this.broadcastService.msalSubject$
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (msg) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
        ),
      )
      .subscribe(({ payload }) => {
        const authResult = payload as AuthenticationResult;
        const account = authResult.account;
        const userClaims = authResult.idTokenClaims as AgrUserClaims;
        const appRole = this.getRoughagePlusAppRole(userClaims);

        this.store.dispatch(
          UserActions.setRoughagePlusAppRole({ role: appRole }),
        );
        this.msalService.instance.setActiveAccount(account);
      });
  }

  public restoreActiveAccount(): void {
    if (!this.getAccount()) {
      const accounts = this.msalService.instance
        .getAllAccounts()
        .filter(
          ({ homeAccountId }) =>
            homeAccountId.indexOf(
              this.environment.SIGNUP_SIGNIN_POLICY.toLowerCase(),
            ) !== -1,
        );
      if (accounts.length > 0) {
        this.msalService.instance.setActiveAccount(accounts[0]);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public login() {
    this.loginRedirectWithAuthority(
      this.selectAuthorityBasedOnHomeAccountId(
        this.getAccount()?.homeAccountId,
      ),
    );
  }

  public loginAsCustomerOrLead() {
    this.loginRedirectWithAuthority(this.b2cAuthorities.customerLeadSignin);
  }

  public loginAsEmployee() {
    this.loginRedirectWithAuthority(this.b2cAuthorities.employeeSignin);
  }

  public signup() {
    this.loginRedirectWithAuthority(this.b2cAuthorities.leadSignup);
  }

  public logout() {
    this.msalService.logoutRedirect();
  }

  private loginRedirectWithAuthority(authority: string | undefined) {
    this.msalService
      .loginRedirect({
        authority,
        scopes: [],
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  public isLoggedIn() {
    return !!this.getAccount();
  }

  public getToken() {
    const account = this.getAccount();
    const authority = this.selectAuthorityBasedOnHomeAccountId(
      account?.homeAccountId,
    );

    return this.msalService.acquireTokenSilent({
      scopes: [],
      account,
      authority,
    });
  }

  public getAccount() {
    return this.msalService.instance.getActiveAccount() ?? undefined;
  }

  private getRoughagePlusAppRole(
    claims: AgrUserClaims,
  ): AccessRoleTypes | undefined {
    const applications =
      claims[AgrUserClaimsProperty.ApplicationsCustomPolicy] ??
      claims[AgrUserClaimsProperty.ApplicationsUserFlow];

    if (applications) {
      const accessRules = decompress(applications) as AccessRule[];
      return accessRules.find(
        (x) =>
          x.ApplicationKey === this.environment.APPLICATION_KEY &&
          x.IsLicenseActive,
      )?.RoleKey;
    }

    return undefined;
  }

  private selectAuthorityBasedOnHomeAccountId(
    homeAccountId: string | undefined,
  ): string | undefined {
    if (!homeAccountId) {
      return undefined;
    }

    if (
      homeAccountId.includes(
        this.environment.SIGNUP_SIGNIN_POLICY.toLowerCase(),
      )
    ) {
      return this.b2cAuthorities.customerLeadSignin;
    }

    if (
      homeAccountId.includes(
        this.environment.EMPLOYEE_SIGNIN_POLICY.toLowerCase(),
      )
    ) {
      return this.b2cAuthorities.employeeSignin;
    }

    return undefined;
  }
}
